import React, { useState, useEffect } from 'react';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import {
    Grid,
    Paper,
    Typography,
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    CircularProgress,
    Backdrop,
    InputAdornment,

} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Business as BusinessIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Search as SearchIcon,
    Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useApiHandler } from '../../hooks/useApiHandler';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import branchService from '../../services/branchService';

const BranchManagement = () => {
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1,
        from: 0,
        to: 0
    });


    const { handleApiCallWithLoading, handleApiCall, showSuccess, showError } = useApiHandler();

    const [branchForm, setBranchForm] = useState({
        branch_name: '',
        branch_address: '',
        status: 'active'
    });

    const handleFormChange = (field, value) => {
        setBranchForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const resetForm = () => {
        setBranchForm({
            branch_name: '',
            branch_address: '',
            status: 'active'
        });
    };

    // Typing in the search box fetches once the user pauses, not per keystroke
    const debouncedSearchTerm = useDebouncedValue(searchTerm);

    // Load data on component mount
    useEffect(() => {
        loadBranches(debouncedSearchTerm);
    }, [page, rowsPerPage, debouncedSearchTerm, statusFilter]);

    const loadBranches = async (search = searchTerm) => {
        setLoading(true);
        try {
            const params = {
                page: page + 1,
                per_page: rowsPerPage,
                search,
                status: statusFilter
            };

            const response = await branchService.getBranches(params);
            if (response.success) {
                setBranches(response.data);
                setPagination(response.meta);
            }
        } catch (error) {
            showError(error.message || 'Failed to load branches');
        } finally {
            setLoading(false);
        }
    };



    const handleAddBranch = () => {
        setSelectedBranch(null);
        resetForm();
        setOpenDialog(true);
    };

    const handleEditBranch = (branch) => {
        setSelectedBranch(branch);
        setBranchForm({
            branch_name: branch.branch_name,
            branch_address: branch.branch_address,
            status: branch.status
        });
        setOpenDialog(true);
    };

    const handleSaveBranch = async () => {
        if (!branchForm.branch_name.trim() || !branchForm.branch_address.trim()) {
            showError('Please fill in all required fields');
            return;
        }

        setLoading(true);
        try {
                            if (selectedBranch) {
                    // Update existing branch
                    const response = await branchService.updateBranch(selectedBranch.branch_id, branchForm);
                    if (response.success) {
                        showSuccess(response.message);
                        loadBranches();
                    }
                } else {
                    // Add new branch
                    const response = await branchService.createBranch(branchForm);
                    if (response.success) {
                        showSuccess(response.message);
                        loadBranches();
                    }
                }
            setOpenDialog(false);
        } catch (error) {
            showError(error.message || 'Failed to save branch');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteBranch = async (branch) => {
        if (window.confirm(`Are you sure you want to delete the branch "${branch.branch_name}"?`)) {
            setLoading(true);
            try {
                const response = await branchService.deleteBranch(branch.branch_id);
                if (response.success) {
                    showSuccess(response.message);
                    loadBranches();
                }
            } catch (error) {
                showError(error.message || 'Failed to delete branch');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearch = (value) => {
        setSearchTerm(value);
    };

    const handleStatusFilter = (value) => {
        setStatusFilter(value);
    };

    const getStatusColor = (status) => {
        return status === 'active' ? 'success' : 'error';
    };

    const getStatusLabel = (status) => {
        return status === 'active' ? 'Active' : 'Inactive';
    };

    return (
        <DashboardLayout title="Branch Management" userType="employee">
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
                <CircularProgress color="inherit" />
            </Backdrop>



            <Paper sx={{ width: '100%' }}>
                {/* Header with search and filters */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                fullWidth
                                placeholder="Search branches..."
                                value={searchTerm}
                                onChange={(e) => handleSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={statusFilter}
                                    label="Status"
                                    onChange={(e) => handleStatusFilter(e.target.value)}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="inactive">Inactive</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={5}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddBranch}
                                >
                                    Add Branch
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Branches Table */}
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Branch Name</TableCell>
                                <TableCell>Branch Address</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Created At</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {branches.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        <Typography variant="body2" color="textSecondary">
                                            No branches found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                branches.map((branch) => (
                                    <TableRow key={branch.branch_id}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <BusinessIcon color="action" />
                                                <Typography variant="body2">
                                                    {branch.branch_name}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ maxWidth: 300 }}>
                                                {branch.branch_address}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={getStatusLabel(branch.status)}
                                                color={getStatusColor(branch.status)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {new Date(branch.created_at).toLocaleDateString()}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                                <Tooltip title="Edit Branch">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEditBranch(branch)}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete Branch">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteBranch(branch)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={pagination.total}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            {/* Add/Edit Branch Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {selectedBranch ? 'Edit Branch' : 'Add New Branch'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Branch Name"
                                value={branchForm.branch_name}
                                onChange={(e) => handleFormChange('branch_name', e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Branch Address"
                                value={branchForm.branch_address}
                                onChange={(e) => handleFormChange('branch_address', e.target.value)}
                                multiline
                                rows={3}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={branchForm.status}
                                    label="Status"
                                    onChange={(e) => handleFormChange('status', e.target.value)}
                                >
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="inactive">Inactive</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)} startIcon={<CancelIcon />}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveBranch}
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={loading}
                    >
                        {selectedBranch ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </DashboardLayout>
    );
};

export default BranchManagement;
